import * as React from 'react';
import format from 'date-fns/format';

import { DATE_FORMAT } from '../app.constants';

/**
 * Compose Provider components.
 * If you have a couple of them and don't want N-level indentation
 * @example
 *   <Router>
 *     <Redux store={store}>
 *       <App />
 *     </Redux>
 *   </Router>
 *   // equal to
 *   const Providers = composeProviders(
 *     <Router />,
 *     <Redux store={store} />,
 *   );
 *
 *   <Providers>
 *     <App />
 *   </Providers>
 */
export function composeProviders(...providers: React.ReactElement[]) {
  return function Providers({ children }: React.PropsWithChildren) {
    const composed = providers.reduceRight(
      (next, prov) => React.cloneElement(prov, { children: next }),
      children
    );

    return <>{composed}</>;
  };
}

type ClassnameChunk = string | Record<string, unknown> | ClassnameChunk[];

/**
 * Concatenate strings into CSS class name aka classnames-lite
 * @example
 *   clx('foo', { bar: true, baz: false }) === 'foo bar'
 */
export function clx(...chunks: ClassnameChunk[]): string {
  return chunks
    .flatMap((chunk) => {
      if (typeof chunk === 'string') {
        return chunk;
      }

      if (Array.isArray(chunk)) {
        return clx(...chunk);
      }

      return Object.entries(chunk)
        .filter(([, value]) => Boolean(value))
        .map(([key]) => key);
    })
    .map((substr) => substr.trim())
    .filter(Boolean)
    .join(' ');
}

type HooksWithMapping = [
  hook: (...args: any[]) => any,
  mapping: string | ((value: any) => Record<string, any>),
  ...args: any[]
];

/**
 * Apply hooks to a Class component.
 * Useful for adapting hooks-only API for Class components,
 * when managing stale closures becomes bothersome
 * @example
 *   withHooks(
 *     [React.useContext, 'theme', ThemeContext],
 *     [useParams, (params) => ({ userId: params.id })]
 *   )(AppComponent);
 *   // props.theme, props.userId
 */
export function withHooks<P, PP>(...hooks: HooksWithMapping[]) {
  return function (BaseComponent: React.ComponentClass<P>) {
    function WithHooks(props: PP) {
      const hooksValues = hooks.reduce((next, [hook, mapping, ...args]) => {
        const value = hook(...args);
        const hookProps =
          typeof mapping === 'string' ? { [mapping]: value } : mapping(value);

        return { ...next, ...hookProps };
      }, {});

      // @ts-ignore
      return <BaseComponent {...props} {...hooksValues} />;
    }

    WithHooks.displayName = `WithHooks(${
      BaseComponent.displayName || BaseComponent.name || 'Component'
    })`;

    return WithHooks;
  };
}

/**
 * Format date to a default string
 * @example
 *   formatDate(1663851029338); // Sep 22 2022, 15:51 PM
 */
export function formatDate(date: string | Date | undefined | null) {
  if (date) {
    return format(new Date(date), DATE_FORMAT);
  }

  return date;
}

/**
 * Comparator for an Array.prototype.sort function
 */
export function compare<T>(a: T, b: T) {
  switch (true) {
    case a > b:
      return 1;
    case a < b:
      return -1;
    default:
      return 0;
  }
}

/**
 * Pick specific properties from an object
 * @example
 *   pick({ a: 1, b: 2, c: 3 }, ['b', 'c']); // { b: 2, c:3 }
 */
export function pick<T extends Record<string, unknown>>(
  object: T,
  keys: string[]
) {
  return Object.fromEntries(
    Object.entries(object).filter(([key]) => keys.includes(key))
  );
}
